import type { VideoJsPlayer } from 'video.js';

import { useCallback, useEffect, useState } from 'react';

import { useLogger } from '../../../contexts/logger-context';
import { VideoJsNativeEvent } from '../videojs-event';

export function useCustomCues({
  player,
  cues,
  onCueChange,
  textTrackLabel,
}: {
  player: VideoJsPlayer;
  cues: TextTrackCue[] | undefined;
  onCueChange: (activeCues: TextTrackCue[]) => void;
  textTrackLabel: string;
}) {
  const { logger } = useLogger();

  const [cuesTrack, setCuesTrack] = useState<TextTrack | undefined>(undefined);

  const isValidExternalCuesTrack = useCallback(
    (track: TextTrack | null): track is TextTrack => {
      return !!(track && track.label === textTrackLabel && track.kind === 'metadata');
    },
    [textTrackLabel]
  );

  const getExternalCuesTrack = useCallback(
    (tracks: TextTrack[]): TextTrack | undefined => {
      return tracks.find((track) => isValidExternalCuesTrack(track));
    },
    [isValidExternalCuesTrack]
  );

  // Detect when external cues track is available
  useEffect(() => {
    let currentTrack: TextTrack | undefined;

    const handleCueChange = () => {
      if (!currentTrack?.activeCues) {
        return;
      }
      const cues = Array.from(currentTrack.activeCues);

      logger.debug(`${textTrackLabel} track: cues changed`, cues);
      onCueChange(cues);
    };

    const onAddTrack = ({ track }: TrackEvent) => {
      if (isValidExternalCuesTrack(track)) {
        logger.debug(`${textTrackLabel} track: added`, track.id);
        currentTrack?.removeEventListener(VideoJsNativeEvent.CUE_CHANGE, handleCueChange);

        setCuesTrack(track);
        currentTrack = track;
        currentTrack.mode = 'hidden';

        // eslint-disable-next-line react-web-api/no-leaked-event-listener
        track.addEventListener(VideoJsNativeEvent.CUE_CHANGE, handleCueChange);
      }
    };

    const onRemoveTrack = ({ track }: TrackEvent) => {
      if (isValidExternalCuesTrack(track)) {
        logger.debug(`${textTrackLabel} track: removed`, track.id);
        currentTrack = undefined;
        setCuesTrack(undefined);
      }
    };

    // Add 'addtrack' and 'removetrack' event listeners
    // eslint-disable-next-line react-web-api/no-leaked-event-listener
    player.textTracks().addEventListener(VideoJsNativeEvent.ADD_TRACK, onAddTrack);
    // eslint-disable-next-line react-web-api/no-leaked-event-listener
    player.textTracks().addEventListener(VideoJsNativeEvent.REMOVE_TRACK, onRemoveTrack);

    return () => {
      player.textTracks().removeEventListener(VideoJsNativeEvent.ADD_TRACK, onAddTrack);
      player.textTracks().removeEventListener(VideoJsNativeEvent.REMOVE_TRACK, onRemoveTrack);
    };
  }, [isValidExternalCuesTrack, logger, onCueChange, player, textTrackLabel]);

  // Add the external cues track to the player
  useEffect(() => {
    const addExternalCuesTrack = () => {
      const existingTrack = getExternalCuesTrack(Array.from(player.remoteTextTracks()));
      if (!existingTrack) {
        player.addRemoteTextTrack(
          {
            id: window.crypto?.randomUUID?.() ?? textTrackLabel,
            kind: 'metadata',
            label: textTrackLabel,
            mode: 'hidden',
            default: false,
          },
          false
        );
      }
    };

    player.on(VideoJsNativeEvent.LOADED_METADATA, addExternalCuesTrack);

    return () => {
      player.off(VideoJsNativeEvent.LOADED_METADATA, addExternalCuesTrack);
    };
  }, [getExternalCuesTrack, player, textTrackLabel]);

  useEffect(() => {
    if (!cuesTrack || !cues) {
      return;
    }

    // Remove all cues from the track
    if (cuesTrack.cues?.length) {
      const externalCues = Array.from(cuesTrack.cues);
      for (const cue of externalCues) {
        cuesTrack.removeCue(cue);
      }
    }

    // Add new cues to the track
    for (const cue of cues) {
      cuesTrack.addCue(cue);
    }
  }, [cuesTrack, cues]);
}
